/**
 * Como uma unidade de oclusão já entregue pela leitura é apresentada.
 *
 * A fila traz uma linha por marcação (`maskId` + `maskTotal` + `occlusionMode`).
 * Este módulo não divide um cartão em várias unidades e não junta linhas que
 * compartilham o modelo. Achar a marcação perguntada é localizar o `maskId`
 * dentro de `masks`, na ordem em que a leitura mandou — a mesma ordem em que
 * serão perguntadas.
 *
 * Sem `maskId` (e sem `askedMask`), a unidade é anterior ao agendamento por
 * marcação: não há número para mostrar, e a face deixa cada marcação revelável.
 */
import type {
  OcclusionModeWire,
  OcclusionStudyMode,
  ReviewCard,
  ReviewMask,
} from '../../../sdk/@types/Revisao';

export interface LeituraOclusao {
  /** Ausente no fio e na tela cai em ocultar todas. */
  mode: OcclusionStudyMode;
  /** Índice em `masks` da marcação perguntada. `null` quando a leitura não nomeou uma. */
  askedIndex: number | null;
  /** "Marcação 2 de 4". `null` quando não há marcação nomeada ou `maskTotal` não veio. */
  rotuloMarcacao: string | null;
}

function texto(value: string | undefined): string {
  return value?.trim() ?? '';
}

/** `hide_one` / `ocultar-uma` são o modo aberto. Qualquer outra coisa, inclusive ausência, é ocultar todas. */
export function modoDeEstudo(card: {
  occlusionMode?: OcclusionModeWire | string;
  studyMode?: OcclusionStudyMode | string;
}): OcclusionStudyMode {
  const raw = card.occlusionMode ?? card.studyMode;
  if (raw === 'hide_one' || raw === 'ocultar-uma') return 'ocultar-uma';
  return 'ocultar-todas';
}

/**
 * Índice da marcação perguntada, ou `null`.
 * `maskId` casa com `masks[].id`. Sem ele, `askedMask` casa por id e depois
 * por geometria e rótulo — formato de fixture, não do `GET /review/due`.
 * Não há queda para a primeira marcação.
 */
export function indicePerguntado(card: {
  masks?: readonly ReviewMask[];
  maskId?: string;
  askedMask?: ReviewMask;
}): number | null {
  const masks = card.masks ?? [];
  const maskId = texto(card.maskId);
  if (maskId) {
    const index = masks.findIndex((mask) => texto(mask.id) === maskId);
    return index >= 0 ? index : null;
  }

  const asked = card.askedMask;
  if (!asked) return null;

  const askedId = texto(asked.id);
  if (askedId) {
    const byId = masks.findIndex((mask) => texto(mask.id) === askedId);
    if (byId >= 0) return byId;
  }

  const byShape = masks.findIndex(
    (mask) =>
      mask.x === asked.x &&
      mask.y === asked.y &&
      mask.w === asked.w &&
      mask.h === asked.h &&
      mask.label === asked.label,
  );
  return byShape >= 0 ? byShape : null;
}

/** "Marcação N de M". M é só `maskTotal`. Sem o total que a leitura mandou, não há rótulo. */
export function rotuloMarcacao(askedIndex: number | null, maskTotal: number | undefined): string | null {
  if (askedIndex === null || askedIndex < 0) return null;
  if (typeof maskTotal !== 'number' || !Number.isInteger(maskTotal) || maskTotal <= 0) return null;
  return `Marcação ${askedIndex + 1} de ${maskTotal}`;
}

/** Modo, índice e rótulo de um cartão. Cartão que não é oclusão não é chamado daqui. */
export function leituraOclusao(
  card: Pick<ReviewCard, 'masks' | 'maskId' | 'maskTotal' | 'occlusionMode' | 'studyMode' | 'askedMask'>,
): LeituraOclusao {
  const askedIndex = indicePerguntado(card);
  return {
    mode: modoDeEstudo(card),
    askedIndex,
    rotuloMarcacao: rotuloMarcacao(askedIndex, card.maskTotal),
  };
}

/** Rótulo da unidade corrente. `null` fora da oclusão, sem marcação nomeada, ou sem `maskTotal`. */
export function rotuloMarcacaoDoCartao(card: ReviewCard): string | null {
  if (card.layout !== 'occlusion') return null;
  return leituraOclusao(card).rotuloMarcacao;
}

/**
 * Chave de uma linha da fila.
 *
 * Duas linhas do mesmo modelo que perguntam marcações diferentes são duas
 * unidades e as duas ficam. A mesma marcação (ou nenhuma nomeada) no mesmo
 * modelo é a corrida que materializa o cartão duas vezes: fica a primeira.
 * Questão continua em `questionId`. Isto não parte uma linha em várias.
 */
export function chaveUnidade(
  card: Pick<ReviewCard, 'id' | 'templateId' | 'questionId' | 'maskId'>,
): string {
  if (card.templateId) {
    const maskId = texto(card.maskId);
    if (maskId) return `${card.templateId}::${maskId}`;
    return card.templateId;
  }
  return card.questionId ?? card.id;
}
